import { useMemo, useState } from 'react';

import { tagsApi, type Tag as TagModel } from '@/api/queries/workspace';
import {
  Button,
  Icon,
  IconButton,
  Table,
  Tag,
  type TableColumn,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { NewTagModal } from './NewTagModal';

type Filter = 'all' | 'manual' | 'auto' | 'namespaced';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'auto', label: 'Auto-applied' },
  { value: 'namespaced', label: 'Namespaced' },
];

export function TagsPage() {
  const { data: tags = [], isPending } = tagsApi.useList();
  const deleteTag = tagsApi.useDelete();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tags.filter((t) => {
      if (filter === 'manual' && t.autoRule) return false;
      if (filter === 'auto' && !t.autoRule) return false;
      if (filter === 'namespaced' && !t.namespace) return false;
      if (q && !t.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tags, filter, query]);

  const stats = useMemo(() => {
    const total = tags.length;
    const auto = tags.filter((t) => !!t.autoRule).length;
    const used = tags.reduce((sum, t) => sum + (t.contactCount ?? 0), 0);
    return { total, auto, used };
  }, [tags]);

  const maxCount = Math.max(1, ...tags.map((t) => t.contactCount ?? 0));

  const columns: TableColumn<TagModel>[] = [
    {
      id: 'name',
      header: 'Tag',
      cell: (tag) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Tag tone="brand" className="" >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: tag.color,
                marginRight: 4,
                display: 'inline-block',
              }}
            />
            {tag.name}
          </Tag>
          {tag.namespace && (
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>· {tag.namespace}</span>
          )}
        </span>
      ),
    },
    {
      id: 'count',
      header: 'Applied',
      width: 220,
      cell: (tag) => (
        <span className="tag-bar">
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{
                width: `${Math.min(100, ((tag.contactCount ?? 0) / maxCount) * 100)}%`,
                background: tag.color,
              }}
            />
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {(tag.contactCount ?? 0).toLocaleString()}
          </span>
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      width: 130,
      cell: (tag) =>
        tag.autoRule ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--brand)' }}>
            <Icon name="sparkles" size={12} /> Auto
          </span>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>Manual</span>
        ),
    },
    {
      id: 'createdBy',
      header: 'Created by',
      width: 160,
      cell: (tag) => tag.createdBy ?? <span style={{ color: 'var(--text-4)' }}>—</span>,
    },
    {
      id: 'lastUsed',
      header: 'Last used',
      width: 140,
      cell: (tag) =>
        tag.lastUsedAt ? (
          new Date(tag.lastUsedAt).toLocaleDateString()
        ) : (
          <span style={{ color: 'var(--text-4)' }}>—</span>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Tags</h1>
          <div className="page-subtitle">
            {stats.total} tags · {stats.auto} auto-applied · {stats.used.toLocaleString()} applications
          </div>
        </div>
        <div className="page-actions">
          <Button variant="primary" leftIcon={<Icon name="plus" size={13} />} onClick={() => setModalOpen(true)}>
            New tag
          </Button>
        </div>
      </header>

      <div className="toolbar">
        <div className="search-input">
          <Icon name="search" size={13} className="si-icon" />
          <input
            value={query}
            placeholder="Search tags…"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search tags"
          />
        </div>
        <div className="audit-filter-group" role="tablist" aria-label="Tag filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={cn('audit-filter', filter === f.value && 'active')}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
          {filtered.length} of {tags.length}
        </span>
      </div>

      <Table<TagModel>
        columns={columns}
        rows={filtered}
        rowKey={(t) => t.id}
        loading={isPending}
        emptyMessage={
          tags.length === 0
            ? 'No tags yet — create your first tag with the button above.'
            : 'No tags match this filter.'
        }
        rowActions={(tag) => (
          <>
            <IconButton
              icon="edit"
              iconSize={13}
              size="sm"
              label="Edit tag"
              className="row-action"
              disabled
              title="Editing UI ships in a follow-up"
            />
            <IconButton
              icon="trash"
              iconSize={13}
              size="sm"
              label="Delete tag"
              className="row-action danger"
              onClick={() => {
                if (confirm(`Delete tag "${tag.name}"? This removes it from all contacts.`)) {
                  deleteTag.mutate(tag.id);
                }
              }}
            />
          </>
        )}
      />

      <NewTagModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
